(() => {
  const DESKTOP_QUERY = '(min-width: 751px)';
  const REDUCED_MOTION_QUERY = '(prefers-reduced-motion: reduce)';
  const controllers = new WeakMap();

  const queueAfterInitialRender = (callback) => {
    const queue = () => {
      if ('requestIdleCallback' in window) {
        window.requestIdleCallback(callback, { timeout: 1500 });
      } else {
        window.setTimeout(callback, 350);
      }
    };

    if (document.readyState === 'complete') queue();
    else window.addEventListener('load', queue, { once: true });
  };

  const hydrateVideoSources = (video) => {
    video.querySelectorAll('source[data-src]').forEach((source) => {
      source.src = source.dataset.src;
      source.removeAttribute('data-src');
    });
  };

  const loadVideo = (section, controller) => {
    const isDesktop = window.matchMedia(DESKTOP_QUERY).matches;
    const video = section.querySelector(
      isDesktop ? '[data-oz-hero-video="desktop"]' : '[data-oz-hero-video="mobile"]'
    );
    if (!video || controller.signal.aborted) return;

    hydrateVideoSources(video);

    const revealVideo = async () => {
      if (controller.signal.aborted) return;
      try {
        await video.play();
        video.classList.add('is-ready');
      } catch (_error) {
        video.classList.remove('is-ready');
      }
    };

    video.addEventListener('canplay', revealVideo, { once: true, signal: controller.signal });
    video.load();
  };

  const hydrateSequenceFrame = async (frame) => {
    if (frame.dataset.srcset) frame.srcset = frame.dataset.srcset;
    if (frame.dataset.src) frame.src = frame.dataset.src;
    frame.removeAttribute('data-srcset');
    frame.removeAttribute('data-src');

    if (frame.decode) {
      try {
        await frame.decode();
      } catch (_error) {
        // The static poster remains visible if a future frame cannot decode.
      }
    }
  };

  const loadSequence = async (section, controller) => {
    const sequence = section.querySelector('[data-oz-hero-sequence]');
    if (!sequence || controller.signal.aborted) return;

    const isDesktop = window.matchMedia(DESKTOP_QUERY).matches;
    if (!isDesktop && sequence.dataset.mobileEnabled !== 'true') return;

    const frames = [...sequence.querySelectorAll('[data-oz-sequence-frame]')];
    if (frames.length < 4) return;

    await Promise.all(frames.map(hydrateSequenceFrame));
    if (controller.signal.aborted) return;

    const duration = Math.max(Number(sequence.dataset.duration) || 3500, 500);
    const delay = Math.max(Number(sequence.dataset.delay) || 0, 0);
    const playOnce = sequence.dataset.playOnce !== 'false';
    const holdFinal = sequence.dataset.holdFinal !== 'false';
    const stepDuration = Math.max(Math.round(duration / Math.max(frames.length - 1, 1)), 120);
    const timers = [];

    const activate = (index) => {
      frames.forEach((frame, frameIndex) => frame.classList.toggle('is-active', frameIndex === index));
    };

    const scheduleCycle = () => {
      frames.forEach((_frame, index) => {
        timers.push(window.setTimeout(() => activate(index), stepDuration * index));
      });

      const cycleEnd = stepDuration * Math.max(frames.length - 1, 1);
      if (!holdFinal) {
        timers.push(window.setTimeout(() => frames.forEach((frame) => frame.classList.remove('is-active')), cycleEnd + stepDuration));
      }
      if (!playOnce) {
        timers.push(window.setTimeout(scheduleCycle, cycleEnd + stepDuration));
      }
    };

    timers.push(window.setTimeout(scheduleCycle, delay));
    controller.signal.addEventListener('abort', () => timers.forEach(window.clearTimeout), { once: true });
  };

  const initialiseSection = (section) => {
    controllers.get(section)?.abort();
    const controller = new AbortController();
    controllers.set(section, controller);

    if (window.matchMedia(REDUCED_MOTION_QUERY).matches) return;

    queueAfterInitialRender(() => {
      if (controller.signal.aborted) return;
      const mode = section.dataset.ozMediaMode || 'video';
      if (mode === 'video') loadVideo(section, controller);
      if (mode === 'sequence') loadSequence(section, controller);
    });
  };

  const initialise = (root = document) => {
    root.querySelectorAll('[data-oz-home-showcase]').forEach(initialiseSection);
  };

  const reinitialiseAll = () => initialise(document);
  window.matchMedia(DESKTOP_QUERY).addEventListener('change', reinitialiseAll);
  window.matchMedia(REDUCED_MOTION_QUERY).addEventListener('change', reinitialiseAll);

  initialise();
  document.addEventListener('shopify:section:load', (event) => initialise(event.target));
  document.addEventListener('shopify:section:unload', (event) => {
    const section = event.target.querySelector('[data-oz-home-showcase]');
    if (section) controllers.get(section)?.abort();
  });
})();
